import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useSavedProperties } from '../hooks/useSavedProperties';
import { toast } from 'sonner';
import { MapPin, Bed, Sofa, Calendar, IndianRupee, Eye, Phone, Mail, ChevronLeft, ChevronRight, Loader2, MessageCircle, Heart, Shield, Share2 } from 'lucide-react';
import ShareListingModal from '../components/ShareListingModal';
import SimilarProperties from '../components/SimilarProperties';
import { trackEvent } from '../utils/track';
const ROLE_BADGE = {
  broker: (
    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200 uppercase tracking-wide" data-testid="detail-lister-tag">
      Broker
    </span>
  ),
  owner: (
    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase tracking-wide" data-testid="detail-lister-tag">
      Owner
    </span>
  ),
};

const API = '/api';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSaved, toggleSave } = useSavedProperties();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImg, setCurrentImg] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // SEO: redirect underscore URLs to hyphen-based canonical URLs (e.g. prop_xxx → prop-xxx)
  useEffect(() => {
    if (id && id.includes('_')) {
      navigate(`/property/${id.replace(/_/g, '-')}`, { replace: true });
    }
  }, [id, navigate]);

  // Convert hyphen-based URL param back to underscore for MongoDB lookups
  const mongoId = id ? id.replace(/-/g, '_') : id;

  const handleSaveToggle = async () => {
    if (!user) { navigate('/login'); return; }
    setSavingToggle(true);
    try {
      const saved = await toggleSave(mongoId);
      toast.success(saved ? 'Saved to favorites!' : 'Removed from saved');
    } catch {
      toast.error('Failed to update saved');
    }
    setSavingToggle(false);
  };

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/properties/${mongoId}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(data => {
        setProperty(data);
        setLoading(false);
        // Track property view with category context
        trackEvent('property_view', {
          city: data.city || '',
          category: data.property_category || '',
          property_type: data.property_type || '',
          rent: String(data.rent || ''),
        });
      })
      .catch(() => { setLoading(false); navigate('/search'); });
  }, [mongoId, navigate]);

  const handleContactClick = async () => {
    if (!user) { navigate('/login'); return; }
    trackEvent('contact_click', { city: property?.city || '', category: property?.property_category || '' });
    try {
      const res = await fetch(`${API}/properties/${mongoId}/contact`, { method: 'POST', credentials: 'same-origin' });
      const data = await res.json();
      if (res.ok) {
        setContactInfo({ phone: data.phone || '', email: data.email || '' });
      }
    } catch (_) { /* show section anyway */ }
    setShowContact(true);
  };

  const handleStartChat = async () => {
    if (!user) { navigate('/login'); return; }
    if (!user.is_phone_verified) {
      toast.error('Please verify your mobile number to use chat');
      navigate('/dashboard');
      return;
    }
    setChatLoading(true);
    try {
      const res = await fetch(`${API}/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ property_id: mongoId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start chat');
      navigate(`/messages?conv=${data.conversation_id}`);
    } catch (err) {
      toast.error(err.message);
    }
    setChatLoading(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );
  if (!property) return null;

  const {
    title, description, city, area, pincode, property_category, property_type,
    bhk_type, floor_details, rent, maintenance, security_deposit, available_from,
    furnishing, bachelor_allowed, pets_allowed, pg_type, food_included, sharing_type,
    carpet_area, amenities,
    images, listing_type, views, clicks, owner_name, owner_email, owner_phone, owner_role,
    status, created_at
  } = property;

  const BADGE_MAP = { featured: 'bg-amber-500', premium: 'bg-purple-600', basic: 'bg-blue-600' };
  const isIndustrialProp = property_category === 'industrial';

  const details = [
    bhk_type && { label: 'BHK', value: bhk_type },
    floor_details && { label: 'Floor', value: floor_details },
    carpet_area && { label: 'Carpet Area', value: `${carpet_area} sq. ft.` },
    !isIndustrialProp && furnishing && { label: 'Furnishing', value: furnishing },
    property_category === 'residential' && { label: 'Bachelor Allowed', value: bachelor_allowed ? 'Yes' : 'No' },
    property_category === 'residential' && { label: 'Pets Allowed', value: pets_allowed ? 'Yes' : 'No' },
    property_category === 'pg' && pg_type && { label: 'PG Type', value: pg_type },
    property_category === 'pg' && { label: 'Food Included', value: food_included ? 'Yes' : 'No' },
    property_category === 'pg' && sharing_type && { label: 'Sharing Type', value: sharing_type },
    available_from && { label: 'Available From', value: new Date(available_from).toLocaleDateString('en-IN') },
    pincode && { label: 'Pincode', value: pincode },
  ].filter(Boolean);

  const siteOrigin = window.location.origin;
  // Canonical URL always uses hyphens (SEO-friendly)
  const canonicalId = mongoId.replace(/_/g, '-');
  const pageUrl = `${siteOrigin}/property/${canonicalId}`;
  // SEO title: always ≤60 chars — dynamically checks if title fits, falls back to compact BHK format
  const pageTitle = (() => {
    const cityStr = city || 'India';
    const suffix = ` for Rent in ${cityStr} | Rent Mitra`;
    const compact = `${bhk_type || ''} ${property_type || ''}`.trim() || 'Rental Property';
    if (!title) return `${compact}${suffix}`.replace(/\s+/g, ' ');
    // Use actual title only if it fits within 60 chars total
    const full = `${title}${suffix}`;
    if (full.length <= 60) return full.replace(/\s+/g, ' ');
    return `${compact}${suffix}`.replace(/\s+/g, ' ');
  })();
  const pageDesc = description
    ? description.slice(0, 160)
    : `${bhk_type || ''} ${property_type || ''} for rent in ${area || ''} ${city || ''}. ₹${rent?.toLocaleString('en-IN')}/month.`.trim();

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteOrigin}/` },
          { "@type": "ListItem", "position": 2, "name": "Search", "item": `${siteOrigin}/search` },
          { "@type": "ListItem", "position": 3, "name": title || `${bhk_type} ${property_type}`, "item": pageUrl }
        ]
      },
      {
        "@type": "Accommodation",
        "name": title || `${bhk_type || ''} ${property_type || ''}`.trim(),
        "description": description || pageDesc,
        "url": pageUrl,
        "image": images && images[0] ? images[0] : undefined,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": area,
          "addressLocality": city,
          "postalCode": pincode,
          "addressCountry": "IN"
        },
        "offers": {
          "@type": "Offer",
          "price": rent,
          "priceCurrency": "INR",
          "availability": status === 'live' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": rent,
            "priceCurrency": "INR",
            "unitText": "MON"
          }
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={title || 'Rental Property | Rent Mitra'} />
        <meta property="og:description" content={description ? description.slice(0, 200) : pageDesc} />
        <meta property="og:image" content={`${process.env.REACT_APP_BACKEND_URL}/api/properties/${mongoId}/og-image`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Rent Mitra" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={`${process.env.REACT_APP_BACKEND_URL}/api/properties/${mongoId}/og-image`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-5">
          <Link to="/" className="hover:text-blue-700">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/search" className="hover:text-blue-700">Search</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-900 font-medium truncate">{title || `${bhk_type} ${property_type}`}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="relative rounded-3xl overflow-hidden bg-slate-100 mb-6" style={{height:'400px'}} data-testid="image-gallery">
              {images && images.length > 0 ? (
                <>
                  <img src={images[currentImg]} alt={title} className="w-full h-full object-cover" />
                  {images.length > 1 && (
                    <>
                      <button onClick={() => setCurrentImg(p => (p - 1 + images.length) % images.length)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center hover:bg-white shadow-lg transition-all" data-testid="prev-image">
                        <ChevronLeft className="w-5 h-5 text-slate-700" />
                      </button>
                      <button onClick={() => setCurrentImg(p => (p + 1) % images.length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center hover:bg-white shadow-lg transition-all" data-testid="next-image">
                        <ChevronRight className="w-5 h-5 text-slate-700" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((img, i) => (
                          <button key={`dot-${i}`} onClick={() => setCurrentImg(i)}
                            className={`w-2 h-2 rounded-full transition-all ${i === currentImg ? 'bg-white w-5' : 'bg-white/60'}`} />
                        ))}
                      </div>
                    </>
                  )}
                  {/* Thumbnail Strip */}
                  {images.length > 1 && (
                    <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Eye className="w-3 h-3" />{views} views
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full img-placeholder flex items-center justify-center">
                  <span className="text-slate-300 text-6xl">🏠</span>
                </div>
              )}
              {BADGE_MAP[listing_type] && (
                <span className={`absolute top-4 left-4 ${BADGE_MAP[listing_type]} text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase`}>
                  {listing_type}
                </span>
              )}
            </div>

            {/* Thumbnail row */}
            {images && images.length > 1 && (
              <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button key={`thumb-${i}`} onClick={() => setCurrentImg(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === currentImg ? 'border-blue-600' : 'border-transparent'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}

            {/* Title & Location */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{fontFamily:'Outfit,sans-serif'}} data-testid="property-detail-title">
                {title || `${bhk_type || ''} ${property_type}`}
              </h1>
              <div className="flex items-center gap-1.5 text-slate-500">
                <MapPin className="w-4 h-4 text-blue-500" />
                <span>{[area, city, pincode].filter(Boolean).join(', ')}</span>
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { label: 'Monthly Rent', value: `₹${Number(rent).toLocaleString('en-IN')}`, highlight: true },
                { label: 'Maintenance', value: maintenance ? `₹${Number(maintenance).toLocaleString('en-IN')}` : 'N/A', highlight: false },
                { label: 'Security Deposit', value: security_deposit ? `₹${Number(security_deposit).toLocaleString('en-IN')}` : 'N/A', highlight: false },
              ].map((item) => (
                <div key={item.label} className={`rounded-2xl p-4 text-center border ${item.highlight ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className={`text-lg font-bold ${item.highlight ? 'text-blue-700' : 'text-slate-800'}`}>{item.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>

            {/* Details Grid */}
            <div className="bg-slate-50 rounded-2xl p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-4" style={{fontFamily:'Outfit,sans-serif'}}>Property Details</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {details.map((d) => (
                  <div key={d.label}>
                    <div className="text-xs text-slate-500 mb-0.5">{d.label}</div>
                    <div className="text-sm font-semibold text-slate-800 capitalize">{d.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities — residential only */}
            {property_category === 'residential' && amenities && amenities.length > 0 && (
              <div className="bg-slate-50 rounded-2xl p-5 mb-6">
                <h3 className="font-bold text-slate-900 mb-3" style={{fontFamily:'Outfit,sans-serif'}}>Amenities</h3>
                <div className="flex flex-wrap gap-2">
                  {amenities.map(a => (
                    <span key={a} className="flex items-center gap-1.5 bg-white border border-blue-100 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {description && (
              <div className="mb-6">
                <h3 className="font-bold text-slate-900 mb-3" style={{fontFamily:'Outfit,sans-serif'}}>About this Property</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
              </div>
            )}

            {/* Share */}
            <div className="border-t border-slate-100 pt-5 mb-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2.5 px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-semibold text-sm transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
                data-testid="share-listing-btn"
              >
                <Share2 className="w-4 h-4" />
                Share Listing
              </button>
              <p className="text-slate-400 text-xs mt-2">Share on WhatsApp, Facebook &amp; Instagram</p>
            </div>
          </div>

          {/* Right Column - Contact Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_4px_20px_rgb(0,0,0,0.06)] p-6 sticky top-20" data-testid="contact-card">
              <div className="text-center mb-5">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-blue-700">{owner_name?.[0]?.toUpperCase() || 'O'}</span>
                </div>
                <h4 className="font-bold text-slate-900">{owner_name}</h4>
                <div className="flex items-center justify-center gap-2 mt-1">
                  {ROLE_BADGE[owner_role] ?? (
                    <p className="text-xs text-slate-500 capitalize">{property_category === 'pg' ? 'PG Owner' : 'Property Owner'}</p>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveToggle}
                disabled={savingToggle}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold mb-4 border-2 transition-all ${
                  isSaved(mongoId)
                    ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                    : 'border-slate-200 text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-500'
                }`}
                data-testid="detail-save-button"
              >
                <Heart className={`w-4 h-4 ${isSaved(mongoId) ? 'fill-current text-red-500' : ''}`} />
                {isSaved(mongoId) ? 'Saved to Favorites' : 'Save Property'}
              </button>

              {showContact ? (
                <div className="space-y-3" data-testid="contact-info">
                  {contactInfo?.phone && (
                    <a href={`tel:${contactInfo.phone}`}
                      className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200 text-green-700 text-sm font-medium hover:bg-green-100 transition-colors">
                      <Phone className="w-4 h-4" />{contactInfo.phone}
                    </a>
                  )}
                  {contactInfo?.email && (
                    <a href={`mailto:${contactInfo.email}`}
                      className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors">
                      <Mail className="w-4 h-4" />{contactInfo.email}
                    </a>
                  )}
                  {contactInfo?.phone && (
                    <a href={`https://wa.me/91${contactInfo.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-green-600 rounded-xl text-white text-sm font-medium hover:bg-green-700 transition-colors">
                      <MessageCircle className="w-4 h-4" />WhatsApp
                    </a>
                  )}
                  {!contactInfo?.phone && !contactInfo?.email && (
                    <p className="text-slate-500 text-sm text-center py-2">Contact details not available.</p>
                  )}
                </div>
              ) : (
                <button onClick={handleContactClick}
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white py-3.5 rounded-xl font-bold text-sm transition-colors mb-3 flex items-center justify-center gap-2"
                  data-testid="show-contact-button">
                  <Phone className="w-4 h-4" />Show Contact Info
                </button>
              )}

              {/* Chat with Owner - hide for own property */}
              {user?.user_id !== property?.owner_id && (
                <div className="mt-2">
                  {!user ? (
                    <Link to="/login" className="w-full border-2 border-blue-700 text-blue-700 hover:bg-blue-50 py-3 rounded-xl font-semibold text-sm transition-colors mb-2 flex items-center justify-center gap-2" data-testid="chat-login-btn">
                      <MessageCircle className="w-4 h-4" />Login to Chat with Owner
                    </Link>
                  ) : !user.is_phone_verified ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center" data-testid="chat-verify-prompt">
                      <div className="flex items-center justify-center gap-1.5 text-amber-700 text-xs font-medium mb-2">
                        <Shield className="w-3.5 h-3.5" />Mobile verification required to chat
                      </div>
                      <Link to="/dashboard" className="text-blue-700 text-xs font-semibold hover:underline">Verify Mobile in Dashboard</Link>
                    </div>
                  ) : (
                    <button
                      onClick={handleStartChat}
                      disabled={chatLoading}
                      className="w-full border-2 border-blue-700 text-blue-700 hover:bg-blue-50 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="start-chat-btn"
                    >
                      {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                      {chatLoading ? 'Opening Chat…' : 'Chat with Owner'}
                    </button>
                  )}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex justify-between text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{views} views</span>
                  <span>{new Date(created_at).toLocaleDateString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <SimilarProperties propertyId={mongoId} />
      </div>
      <Footer />

      {/* Share Modal */}
      {showShareModal && (
        <ShareListingModal property={property} onClose={() => setShowShareModal(false)} />
      )}
    </div>
  );
}
